import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from '@services/analytics-service';
import { AnalyticEvents } from '@constants/index';
import { SarStore, type ConcernLocationDraft } from '@store/sar-store';
import { Location } from '../../constants';
import { uploadDamageImages } from '../../services/damage-location-service';
import type { DamagePin } from './useDamagePins';

/*
  The on-Confirm background pipeline, extracted from SpinnerLocation so the view only has to
  fire it and navigate. Runs AFTER navigation (fire-and-forget): guarantee the bullseye
  images, upload them (server-side LLM eval + Document Manager — NO DB write here), swap the
  'Other' placeholder for the detected panel, emit analytics + a backup clickstream record,
  and RETURN the draft the coverage step will persist once the asrId exists. Never throws —
  on failure it still emits funnel-parity analytics and resolves to null.
*/

// PanelLabels (server) and the Exterior RepairIssueLocation rows share these exact names,
// but panelName arrives as a free string. Validate membership before trusting it as a
// Location so a backend label drift (casing/spacing/new panel) can't slip an invalid value
// into user selections — fall back to the 'Other' placeholder instead.
const LOCATION_VALUES = new Set<string>(Object.values(Location));
const isLocation = (value: string): value is Location => LOCATION_VALUES.has(value);

export type PanelOutcome = 'mapped' | 'off-vehicle' | 'model-failed' | 'no-panel-returned' | 'unmappable';

/*
  Four different outcomes all record the 'Other' placeholder, and they have different owners — so
  name the cause rather than lumping them together. (PROD split is roughly 542 unmappable vs 44
  model-failed vs 8 off-vehicle: the "customer mis-tapped" explanation that gets assumed is by far
  the smallest slice.)

    off-vehicle       the model says the tap wasn't on the car — correct behaviour, a UX signal.
    model-failed      the model request or reply-parse failed ('Unknown') — engineering/vendor signal.
    no-panel-returned nothing came back (blank capture, failed upload, or empty response).
    unmappable        the model named a panel that isn't an accepted Location — label drift. This is
                      the one to alarm on: a known-good answer is silently discarded.
*/
export function classifyDetectedPanel(panelName: string | null | undefined): PanelOutcome {
  const name = panelName?.trim();
  if (!name) return 'no-panel-returned';
  if (name === 'Not on vehicle') return 'off-vehicle';
  if (name === 'Unknown') return 'model-failed';
  return isLocation(name) ? 'mapped' : 'unmappable';
}

export interface ConfirmUploadContext {
  stockNumber: number;
  imagePipeline: 'curated' | 'stabilized' | null;
  spinStyle: string | null;
  // Guarantees the pin's bullseye images exist (runs the deferred capture now if needed).
  captureImages: () => Promise<{ full: string; zoom: string }>;
  updateUserSelections: (selection: { location: Location }) => void;
}

export async function runConfirmUpload(pin: DamagePin, ctx: ConfirmUploadContext): Promise<ConcernLocationDraft | null> {
  const ld = pin.locationData;
  const frameUrl = ld.frameImageUrl || '';
  const referenceFrameName = frameUrl ? frameUrl.split('/').pop()?.split('?')[0] || frameUrl : '';
  try {
    const { full, zoom } = await ctx.captureImages();
    const upload = await uploadDamageImages({
      fullImage: full,
      zoomImage: zoom,
      stockNumber: ctx.stockNumber,
      // Modal-session correlation key, persisted on the ConcernLocation row (read at call
      // time from the live store proxy).
      sarSessionId: SarStore.sarSessionId,
      resolvedSide: ld.resolvedSide,
      viewDescription: ld.viewDescription,
      angleDeg: ld.angleDeg,
      frameIndex: ld.frameIndex,
      referenceFrameName,
      referenceFrameUrl: frameUrl,
      positionPct: ld.positionPct,
      canvasFraction: pin.canvasFraction,
      containerSize: ld.containerSize,
      capturedAt: pin.timestamp,
    });

    // The full record the coverage step persists once saveuserselections returns the asrId
    // (via save-concern-location — a single insert per table, ASR id set from the start).
    const draft: ConcernLocationDraft = {
      sarSessionId: SarStore.sarSessionId,
      stockNumber: ctx.stockNumber,
      panelId: upload.panelId,
      panelName: upload.panelName,
      resolvedSide: ld.resolvedSide,
      confidence: upload.confidence,
      description: upload.description,
      viewDescription: ld.viewDescription,
      angleDeg: ld.angleDeg,
      frameIndex: ld.frameIndex,
      referenceFrameName,
      referenceFrameUrl: frameUrl,
      positionPct: ld.positionPct,
      canvasFraction: pin.canvasFraction,
      containerSize: ld.containerSize,
      fullImageUrl: upload.fullImageUrl,
      zoomImageUrl: upload.zoomImageUrl,
      fullDocumentId: upload.fullDocumentId,
      zoomDocumentId: upload.zoomDocumentId,
      aiTimeMs: upload.aiTimeMs,
      capturedAt: pin.timestamp,
    };

    const panelOutcome = classifyDetectedPanel(upload.panelName);
    const detectedLocation =
      panelOutcome === 'mapped' && upload.panelName && isLocation(upload.panelName) ? upload.panelName : null;
    if (detectedLocation) ctx.updateUserSelections({ location: detectedLocation });

    // Every fall back to 'Other' is reported with WHY, so the causes can be told apart from
    // telemetry alone instead of reading PanelName out of the DB. Alarm on reason:unmappable — that
    // is the one where a known-good answer is silently discarded (label drift). imagesCaptured
    // separates a failed client capture from a failed upload/empty response within
    // 'no-panel-returned', which the panel name alone cannot distinguish.
    if (panelOutcome !== 'mapped') {
      const otherRecorded = {
        variant: 'spinner',
        reason: panelOutcome,
        panelName: upload.panelName ?? null,
        resolvedSide: ld.resolvedSide,
        confidence: upload.confidence,
        imagesCaptured: !!full,
        aiTimeMs: upload.aiTimeMs,
      };
      Analytics.trackEvent(AnalyticEvents.sarLocationOtherRecorded, otherRecorded);
      datadogRum.addAction(AnalyticEvents.sarLocationOtherRecorded, otherRecorded);
    }

    Analytics.trackEvent(AnalyticEvents.sarLocationSelectLocation, {
      location: detectedLocation ?? Location.Other,
      variant: 'spinner',
    });

    // Funnel event — non-sensitive metrics/booleans + opaque ids only. The Document
    // Manager links/ids and the model's free-text description are deliberately NOT here.
    const payload = {
      variant: 'spinner',
      stockNumber: ctx.stockNumber,
      imagePipeline: ctx.imagePipeline,
      spinStyle: ctx.spinStyle,
      panelName: upload.panelName,
      panelId: upload.panelId,
      resolvedSide: ld.resolvedSide,
      confidence: upload.confidence,
      viewDescription: ld.viewDescription,
      angleDeg: ld.angleDeg,
      frameIndex: ld.frameIndex,
      positionPct: ld.positionPct,
      canvasFraction: pin.canvasFraction,
      containerSize: ld.containerSize,
      referenceFrameName,
      referenceFrameUrl: frameUrl,
      imagesUploaded: !!upload.fullImageUrl,
      aiTimeMs: upload.aiTimeMs,
    };
    Analytics.trackEvent(AnalyticEvents.sarLocationSpinnerConfirmed, payload);
    datadogRum.addAction(AnalyticEvents.sarLocationSpinnerConfirmed, payload);

    // Backup record: since the DB write now happens later (coverage step) and can be lost,
    // emit the FULL record to clickstream so it still lands in Snowflake as a fallback.
    // Includes the model's description, the Document Manager document ids (durable handles),
    // AND the secure image URLs (per product decision — kept even though they're
    // time-limited and expire; the doc ids remain the durable handle for retrieval).
    const backup = {
      ...payload,
      description: upload.description,
      fullDocumentId: upload.fullDocumentId,
      zoomDocumentId: upload.zoomDocumentId,
      fullImageUrl: upload.fullImageUrl,
      zoomImageUrl: upload.zoomImageUrl,
      capturedAt: pin.timestamp,
    };
    Analytics.trackEvent(AnalyticEvents.sarLocationConcernRecord, backup);

    return draft;
  } catch (err) {
    datadogRum.addAction('sar-spinner-confirm-bg-error', {
      error: err instanceof Error ? err.message : String(err),
    });
    // Funnel parity even when the background work dies: the selection stands at the 'Other'
    // placeholder set at confirm time.
    Analytics.trackEvent(AnalyticEvents.sarLocationSelectLocation, {
      location: Location.Other,
      variant: 'spinner',
    });
    return null;
  }
}
